/**
 * Rhythmz — SettingsPanel
 * ───────────────────────
 * UI component for configuring AI model settings.
 * Allows selecting providers, entering API keys,
 * configuring custom endpoints, and testing connections.
 */

import { modelSettings, type ProviderType } from './ModelSettings';

export class SettingsPanel {
  private container: HTMLElement;
  private onSettingsChange: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  /** Set callback when settings change. */
  onChange(cb: () => void): void {
    this.onSettingsChange = cb;
  }

  /** Render the settings panel into the container. */
  render(): void {
    this.container.innerHTML = `
      <div class="settings-overlay" id="settings-overlay" style="display:none">
        <div class="settings-panel">
          <div class="settings-header">
            <span class="settings-title">⚙ AI Model Settings</span>
            <button class="settings-close" id="settings-close">✕</button>
          </div>
          <div class="settings-body">
            <div class="settings-notice">
              <strong>Default:</strong> Free Cloudflare Workers AI — no setup needed.
              Add API keys below for access to more powerful models.
            </div>

            <!-- Provider Selection -->
            <div class="settings-section">
              <div class="settings-section-title">Provider</div>
              <select id="settings-provider" class="settings-select">
                <option value="workers-ai">Cloudflare Workers AI (free)</option>
                <option value="groq">Groq AI</option>
                <option value="gemini">Google Gemini</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="custom">Custom Endpoint</option>
              </select>
            </div>

            <!-- API Key Inputs -->
            <div class="settings-section" id="settings-keys-section">
              <div class="settings-section-title">API Keys</div>
              <div class="settings-key-row">
                <label>Groq API Key</label>
                <input type="password" id="settings-key-groq" class="settings-input" placeholder="gsk_..." />
              </div>
              <div class="settings-key-row">
                <label>Gemini API Key</label>
                <input type="password" id="settings-key-gemini" class="settings-input" placeholder="AIzaSy..." />
              </div>
              <div class="settings-key-row">
                <label>OpenAI API Key</label>
                <input type="password" id="settings-key-openai" class="settings-input" placeholder="sk-..." />
              </div>
              <div class="settings-key-row">
                <label>Anthropic API Key</label>
                <input type="password" id="settings-key-anthropic" class="settings-input" placeholder="sk-ant-..." />
              </div>
            </div>

            <!-- Custom Endpoint -->
            <div class="settings-section" id="settings-custom-section" style="display:none">
              <div class="settings-section-title">Custom Endpoint</div>
              <div class="settings-key-row">
                <label>Endpoint URL</label>
                <input type="url" id="settings-custom-endpoint" class="settings-input"
                  placeholder="http://localhost:11434/v1/chat/completions" />
                <div class="settings-hint">OpenAI-compatible API endpoint (Ollama, LM Studio, vLLM, OpenRouter, etc.)</div>
              </div>
              <div class="settings-key-row">
                <label>Model Name</label>
                <input type="text" id="settings-custom-model" class="settings-input" placeholder="llama3.2" />
                <div class="settings-hint">The model name your endpoint expects.</div>
              </div>
              <div class="settings-key-row">
                <label>API Key (if required)</label>
                <input type="password" id="settings-key-custom" class="settings-input" placeholder="Optional" />
              </div>
            </div>

            <!-- Local Model Toggle -->
            <div class="settings-section">
              <div class="settings-section-title">Local Model (WebLLM)</div>
              <label class="settings-toggle">
                <input type="checkbox" id="settings-use-local" />
                <span>Use local WebLLM model in browser</span>
              </label>
              <div class="settings-key-row" id="settings-local-model-row">
                <label>WebLLM Model ID</label>
                <input type="text" id="settings-local-model" class="settings-input"
                  placeholder="Phi-4-mini-instruct-q4f16_1-MLC" />
              </div>
            </div>

            <!-- Test Connection -->
            <div class="settings-section">
              <button id="settings-test" class="settings-test-btn">Test Connection</button>
              <div id="settings-test-result" class="settings-test-result"></div>
            </div>

            <!-- Actions -->
            <div class="settings-actions">
              <button id="settings-reset" class="settings-reset-btn">Reset to Defaults</button>
              <button id="settings-save" class="settings-save-btn">Save Settings</button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
    this.loadCurrentSettings();
  }

  /** Show the settings overlay. */
  show(): void {
    const overlay = this.container.querySelector('#settings-overlay') as HTMLElement;
    if (overlay) {
      this.loadCurrentSettings();
      overlay.style.display = 'flex';
    }
  }

  /** Hide the settings overlay. */
  hide(): void {
    const overlay = this.container.querySelector('#settings-overlay') as HTMLElement;
    if (overlay) overlay.style.display = 'none';
  }

  /** Bind UI events. */
  private bindEvents(): void {
    const overlay = this.container.querySelector('#settings-overlay') as HTMLElement;

    // Close
    const closeBtn = overlay.querySelector('#settings-close') as HTMLElement;
    closeBtn?.addEventListener('click', () => this.hide());

    // Click outside to close
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) this.hide();
    });

    // Provider dropdown
    const provider = overlay.querySelector('#settings-provider') as HTMLSelectElement | null;
    provider?.addEventListener('change', () => this.onProviderChange());

    // Local model toggle
    const useLocal = overlay.querySelector('#settings-use-local') as HTMLInputElement | null;
    const localRow = overlay.querySelector('#settings-local-model-row') as HTMLElement | null;
    useLocal?.addEventListener('change', () => {
      if (localRow) localRow.style.display = useLocal.checked ? 'block' : 'none';
    });

    // Test connection
    const testBtn = overlay.querySelector('#settings-test') as HTMLElement;
    testBtn?.addEventListener('click', () => this.testConnection());

    // Save
    const saveBtn = overlay.querySelector('#settings-save') as HTMLElement;
    saveBtn?.addEventListener('click', () => this.saveSettings());

    // Reset
    const resetBtn = overlay.querySelector('#settings-reset') as HTMLElement;
    resetBtn?.addEventListener('click', () => this.resetSettings());
  }

  /** Load current settings into UI. */
  private loadCurrentSettings(): void {
    const s = modelSettings.getAll();
    const overlay = this.container.querySelector('#settings-overlay') as HTMLElement;

    const setVal = (id: string, val: string) => {
      const el = overlay.querySelector(`#${id}`) as HTMLInputElement | HTMLSelectElement;
      if (el) el.value = val;
    };
    const setChecked = (id: string, val: boolean) => {
      const el = overlay.querySelector(`#${id}`) as HTMLInputElement;
      if (el) el.checked = val;
    };

    setVal('settings-provider', s.provider);
    setVal('settings-key-groq', s.apiKeys.groq ?? '');
    setVal('settings-key-gemini', s.apiKeys.gemini ?? '');
    setVal('settings-key-openai', s.apiKeys.openai ?? '');
    setVal('settings-key-anthropic', s.apiKeys.anthropic ?? '');
    setVal('settings-custom-endpoint', s.customEndpoint);
    setVal('settings-custom-model', s.customModelName);
    setVal('settings-local-model', s.localModelId);
    setChecked('settings-use-local', s.useLocalModel);

    this.onProviderChange();
  }

  /** Handle provider dropdown change. */
  private onProviderChange(): void {
    const overlay = this.container.querySelector('#settings-overlay') as HTMLElement;
    const provider = (overlay?.querySelector('#settings-provider') as unknown as HTMLSelectElement)?.value ?? 'workers-ai';
    const customSection = overlay?.querySelector('#settings-custom-section') as HTMLElement | null;
    const keysSection = overlay?.querySelector('#settings-keys-section') as HTMLElement | null;
    const localRow = overlay?.querySelector('#settings-local-model-row') as HTMLElement | null;
    const useLocal = overlay?.querySelector('#settings-use-local') as HTMLInputElement | null;

    if (customSection) customSection.style.display = provider === 'custom' ? 'block' : 'none';
    if (keysSection) keysSection.style.display = provider !== 'custom' && provider !== 'workers-ai' ? 'block' : 'none';
    if (localRow) localRow.style.display = useLocal?.checked ? 'block' : 'none';
  }

  /** Save all settings from the UI. */
  private saveSettings(): void {
    const overlay = this.container.querySelector('#settings-overlay') as HTMLElement;

    const val = (id: string): string =>
      (overlay.querySelector(`#${id}`) as HTMLInputElement)?.value ?? '';
    const checked = (id: string): boolean =>
      (overlay.querySelector(`#${id}`) as HTMLInputElement)?.checked ?? false;

    modelSettings.update({
      provider: val('settings-provider') as ProviderType,
      apiKeys: {
        groq: val('settings-key-groq') || undefined,
        gemini: val('settings-key-gemini') || undefined,
        openai: val('settings-key-openai') || undefined,
        anthropic: val('settings-key-anthropic') || undefined,
      },
      customEndpoint: val('settings-custom-endpoint'),
      customModelName: val('settings-custom-model'),
      useLocalModel: checked('settings-use-local'),
      localModelId: val('settings-local-model'),
    });

    // Show confirmation
    const result = overlay.querySelector('#settings-test-result') as HTMLElement;
    if (result) {
      result.textContent = '✅ Settings saved.';
      result.className = 'settings-test-result success';
    }

    this.onSettingsChange?.();
  }

  /** Reset settings to defaults. */
  private resetSettings(): void {
    modelSettings.reset();
    this.loadCurrentSettings();

    const overlay = this.container.querySelector('#settings-overlay') as HTMLElement;
    const result = overlay.querySelector('#settings-test-result') as HTMLElement;
    if (result) {
      result.textContent = '🔄 Reset to defaults.';
      result.className = 'settings-test-result success';
    }

    this.onSettingsChange?.();
  }

  /** Test the connection to the currently configured provider. */
  private async testConnection(): Promise<void> {
    const overlay = this.container.querySelector('#settings-overlay') as HTMLElement;
    const result = overlay.querySelector('#settings-test-result') as HTMLElement;
    const testBtn = overlay.querySelector('#settings-test') as HTMLButtonElement;

    if (!result) return;
    result.textContent = '⏳ Testing connection...';
    result.className = 'settings-test-result';
    testBtn.disabled = true;

    try {
      const resp = await fetch('/ai/generate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          prompt: 'Reply with exactly: OK',
          max_tokens: 10,
          temperature: 0,
          ...modelSettings.getRequestPayload(),
        }),
      });

      if (resp.ok) {
        const data: any = await resp.json();
        result.textContent = `✅ Connected! Response: "${(data.result ?? 'OK').slice(0, 50)}"`;
        result.className = 'settings-test-result success';
      } else {
        const err = await resp.text();
        result.textContent = `❌ Connection failed: ${err.slice(0, 100)}`;
        result.className = 'settings-test-result error';
      }
    } catch (e) {
      result.textContent = `❌ Error: ${e instanceof Error ? e.message : String(e)}`;
      result.className = 'settings-test-result error';
    } finally {
      testBtn.disabled = false;
    }
  }
}